// Shared metadata helpers for the Community Showcase UI.
// Resolves a stored `source_tool` slug (which may match either a tool's
// featureKey like "itemimage" or its href slug like "item-image") to display
// info, and exposes the category chips used by the gallery filters.

import { GalleryHorizontalEnd, type LucideIcon } from "lucide-react";
import { HUB_CATEGORIES, type HubCategoryId, type HubTool } from "@/components/hub/hubData";

export type ShowcaseToolMeta = { title: string; icon: LucideIcon; href: string | null };

const TOOL_BY_SLUG: Map<string, HubTool> = (() => {
  const map = new Map<string, HubTool>();
  for (const category of HUB_CATEGORIES) {
    for (const tool of category.tools) {
      const hrefSlug = tool.href.replace(/^\//, "");
      map.set(hrefSlug, tool);
      if (tool.featureKey) map.set(tool.featureKey, tool);
    }
  }
  return map;
})();

const CATEGORY_BY_TOOL_HREF: Map<string, HubCategoryId> = (() => {
  const map = new Map<string, HubCategoryId>();
  for (const category of HUB_CATEGORIES) {
    for (const tool of category.tools) map.set(tool.href, category.id);
  }
  return map;
})();

/** "Made with X" — resolves a source_tool slug to a title + icon (+ tool link). */
export function resolveShowcaseTool(sourceTool: string | null | undefined): ShowcaseToolMeta {
  if (sourceTool) {
    const tool = TOOL_BY_SLUG.get(sourceTool) ?? TOOL_BY_SLUG.get(sourceTool.toLowerCase());
    if (tool) return { title: tool.title, icon: tool.icon, href: tool.href };
  }
  return { title: "The Workshop", icon: GalleryHorizontalEnd, href: null };
}

/**
 * Best-effort category for an item that didn't get one explicitly at publish.
 * The Showcase UI no longer exposes categories, but the server still stamps one
 * on publish (via this helper) so the stored data stays meaningful.
 */
export function categoryForTool(sourceTool: string | null | undefined): HubCategoryId | null {
  if (!sourceTool) return null;
  const tool = TOOL_BY_SLUG.get(sourceTool) ?? TOOL_BY_SLUG.get(sourceTool.toLowerCase());
  return tool ? CATEGORY_BY_TOOL_HREF.get(tool.href) ?? null : null;
}
